import { ViewType, ViewObject } from '../base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { dataService } from '../../data';
import { TicketProduct } from '../objects/ticketproduct';

import { TicketView } from './ticket';
import { TicketOption } from '../objects/ticketoption';
import { TicketOfferView } from './ticketoffer';
import { ProductView } from './product';
import { PreselectView } from './preselect';
import { ProductOptView } from './productopt';

export interface TicketProductInfo {
    price: number,          // this is the original price without offers
    offer: number | null,   // this is the ticketproduct price inside the applied offer
    final: number | null;   // this is the ticketproduct price for the non included options in the offer
    fixed: number | null,   // this is the aplied fixed price

    amount: number,         // the number of grouped products in this entry  
    charge: number,         // total amount to be charged for the ticket product (for single product)
    refund: number          // total amount to be refunded for the returned ticket product
}

/****************************/
/* CLASS VIEW               */
/****************************/

export class _TicketProductView extends ViewObject<TicketProduct> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get ticketproduct(): TicketProduct{
        return this.object;
    }
             
    constructor(object: TicketProduct, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _calctrackhash: string | null = null;
    private _calcchargeinfo: TicketProductInfo | null = null;
    private _calcprodtprice: number | null = null;   // raw __prodtprice behind calcinfo.price (null kept)
    private _calcpriceinfo: PriceType | null = null;
    private _optnspriceinfo: PriceType | null = null;      
    private _validoptions: Array <TicketOption> | null = null;
    private _productoptions: Array <ProductOptView> | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('TICKETPRODUCT')){
            this._calctrackhash = null;
            this._calcchargeinfo = null;
            this._calcprodtprice = null;
            this._calcpriceinfo = null;
            this._optnspriceinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETOFFER')){
            this._calctrackhash = null;
            this._calcchargeinfo = null;
            this._calcprodtprice = null;
            this._calcpriceinfo = null;
            this._optnspriceinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('TICKETOPTION')){
            this._calctrackhash = null;
            this._calcchargeinfo = null;
            this._calcprodtprice = null;
            this._calcpriceinfo = null;
            this._optnspriceinfo = null;
            this._validoptions = null;
            this._productoptions = null;
            this.DoRefreshView();
        }

        if (targets.includes('QRCODE')){
            this._calctrackhash = null;
            this._calcchargeinfo = null;
            this._calcprodtprice = null;
            this._calcpriceinfo = null;
            this._optnspriceinfo = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];
        
        // register to updates on contained items        
        this._subscriptions.push(
            this.ticketproduct.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        )    

        if (this.ticket){
            this._subscriptions.push(
                this.ticket.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }                
                })
            )        
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/   
    
    get ticketoptions(): Array <TicketOption> {
        if (this._validoptions === null){
            const _validoptions: Array <TicketOption> = [];

            for(const _option of this.ticketproduct.options){
                if (_option.IsValid){
                    _validoptions.push(_option);
                }                    
            }

            this._validoptions = _validoptions;
        }
        
        return this._validoptions;
    }

    get options(): Array <ProductOptView> {
        if (this._productoptions === null){
            const _productoptions: Array <ProductOptView> = []; 
            
            for (const ticketoption of this.ticketoptions){
                _productoptions.push(this.alives.get(ticketoption.option) as ProductOptView); 
            }

            this._productoptions = _productoptions;
        }
        return this._productoptions;
    }

    get product(): ProductView | null {
        return this.alives.get(this.ticketproduct.product) as ProductView;
    }

    get type(): string | null {
        return this.product?.type ?? this.ticketproduct.type;
    }

    get offer(): TicketOfferView | null {
        return this.alives.get(this.ticketproduct.offer) as TicketOfferView;
    }

    set offer(value: TicketOfferView | null){
        const _prevoffer = this.ticketproduct.offer;
        if (_prevoffer){
            _prevoffer.DelProduct(this.ticketproduct);
            _prevoffer.status = 'DE';
        }

        const _nextoffer = value?.object || null;
        if (_nextoffer){
            _nextoffer.AddProduct(this.ticketproduct);
        }

        this.ticketproduct.offer = _nextoffer;
    }

    get ticket(): TicketView | null {
        return this.alives.get(this.ticketproduct.ticket) as TicketView;
    }

    /**********************************/
    /* TICKET PRODUCT PRICE           */
    /**********************************/

    get priceinfo(): PriceType {
        if (this._calcpriceinfo === null){
            const _p:number = this.ticketproduct.charge || 0;
            const _r:number = this.ticketproduct?.taxrate ?? AppConstants.defaultTaxRate;

            this._calcpriceinfo = new PriceInfo(_p, _r).price;
        }

        return this._calcpriceinfo;
    }

    get optnsprice(): PriceType {
        if (this._optnspriceinfo === null){
            const _p:number = this.__optnsprice;
            const _r:number = this.ticketproduct?.taxrate ?? AppConstants.defaultTaxRate;

            this._optnspriceinfo = new PriceInfo(_p, _r).price;
        }

        return this._optnspriceinfo;
    }

    get calcinfo(): TicketProductInfo {
        if (this._calcchargeinfo === null){
            const _productprice: number | null = this.__prodtprice;  // ticketproduct total price
            const _productfixed: number | null = this.__fixedprice;  // ticketproduct manually fixed price
            const _productfinal: number | null = this.__finalprice;  // ticketproduct price for non-offered options 

            let _productoffer: number | null = null;                 // ticketproduct relative price inside an applied offer
            if (this.offer){
                const _prodtprice = this.__fixedprice ?? this.__prodtprice ?? 0;
                const _offerprice = this.__offerprice ?? 0;
                const _totalprice = this.__totalprice ?? 0;

                if (_totalprice){   // proportional price for the product for the applied offer price
                    _productoffer = (_prodtprice * _offerprice) / _totalprice;
                }
            }

            /* charge the fixed price or charge the offer price or charge the product price */
            const _charge = _productfixed ?? _productoffer ?? _productprice ?? 0;
            const _refund = this.ticketproduct.refund ?? 0;

            this._calcprodtprice = _productprice;
            this._calcchargeinfo = {
                price: _productprice || 0,
                offer: _productoffer,
                final: _productfinal,
                fixed: _productfixed,

                amount: this.ticketproduct.amount || 0,
                charge: (_charge - _refund) || 0,
                refund: this.ticketproduct.refund || 0
            }
        }

        return this._calcchargeinfo;
    }

    /**
     * Stores the calculated price and charge on the line.
     *
     * Reserved for the ticket recalculation flow. A calculated charge cannot
     * always round-trip through storage: a proportional offer price loses
     * precision and a zero charge is stored as no value at all, so writing it
     * outside an explicit recalculation would leave the line pending commit
     * on every load.
     *
     * A manually fixed price is held on `charge` as the amount the operator
     * entered, and the calculation reads it back from there. Storing the
     * calculated charge over it would feed the calculation its own output and
     * deduct the refund once more on every pass.
     */
    Recalculate(): void {
        this._calcchargeinfo = null;
        this._calcprodtprice = null;

        const _calcchargeinfo = this.calcinfo;
        const _calcprodtprice = this._calcprodtprice;

        this.ticketproduct.price = _calcprodtprice;
        if (!this.ticketproduct.fixed){
            this.ticketproduct.charge = _calcchargeinfo.charge;
        }

        this._calcchargeinfo = _calcchargeinfo;
        this._calcprodtprice = _calcprodtprice;
    }

    /**********************************/
    /* GROUPING FOR DISPLAY           */
    /**********************************/

    private _display_to: TicketProductView | null = null;   // this ticketproduct is displayed in _display_to
    private _display_fr: TicketProductView | null = null;   // this ticketproduct is displaying _display_fr
    
    get Display(): boolean {
        return (this._display_to == null);
    }

    get DisplayTo(): TicketProductView | null {
        return this._display_to;
    }

    set DisplayTo(value: TicketProductView | null){
        const _prev = this._display_to;
        if (_prev){
            _prev.DisplayFr = null;
        }

        const _next = value;
        if (_next){
            _next.DisplayFr = this.alives.get(this.ticketproduct) as TicketProductView;
        }

        this._display_to = _next;

        this._calctrackhash = null;
        this.DoRefreshView();
    }

    get DisplayFr(): TicketProductView | null {
        return this._display_fr;
    }

    set DisplayFr(value: TicketProductView | null){
        this._display_fr = value;

        this._calctrackhash = null;
        this.DoRefreshView();
    }

    HasEvent(event: string): boolean {
        return this.ticketproduct.HasEvent(event);
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    /* calculate a hash based on the content to avoid unnecesary rendering operations */
    get _hash(): string {
        if (!this._calctrackhash){
            let _hash = '';

            _hash += (this.Display) ? '1' : '0';                      // product display status
            _hash += "|" + (this.ticketproduct.product?.name ?? '');  // product name (debugging)

            _hash += "|" + (this.ticketproduct.status ?? 'STATUS');   // same ticketproduct status
            _hash += "|" + (this.ticketproduct.refund ?? 'REFUND');   // same ticketproduct refund
            _hash += "|" + (this.ticketproduct.market ?? 'MARKET');   // same ticketproduct market price
            _hash += "|" + (this.ticketproduct.weight ?? 'WEIGHT');   // same ticketproduct weight
            _hash += "|" + (this.ticketproduct.charge ?? 'CHARGE');   // same ticketproduct charge price
            _hash += "|" + (this.ticketproduct.comments ?? '');       // same ticketproduct comments
    
            _hash += "|" + (this.section ?? '0');                     // same ticketproduct section
            _hash += "|" + (this.offer?.offer?._uuid ?? 'OFFER');     // same applied offer instance         
            _hash += "|" + (this.ticket?._uuid ?? 'TICKET');          // same ticket instance
            _hash += "|" + (this.product?._uuid ?? 'PRODUCT');        // same product instance
    
            _hash += "|" + (this.options.length);                     // same selected options
            for (const _option of this.options){
                _hash += "|" + (_option?._uuid ?? 'OPTION');
            }

            this._calctrackhash = _hash;
        }

        return this._calctrackhash;
    }

    get section(): number {
        let _section = 0;

        if (this.ticket){
            const ordered = [...this.ticket.products].sort((a, b) => (a.sort || 0) - (b.sort || 0));
            for (const ticketproduct of ordered){
                if (ticketproduct.object === this.ticketproduct){
                    return _section;
                }

                if (ticketproduct.IsSplitter){
                    _section++;
                }
            }
        }

        return _section;
    }

    get IsInvite(): boolean {
        return (this.ticketproduct.fixed === true) && (this.ticketproduct.charge === 0);
    }

    IsProduct(product: ProductView): boolean {
        return (this.product == product);
    }

    IsDirect(direct: PreselectView): boolean {
        if (this.product != direct.product){
            return false;   // not same product
        }

        for(const _directoption of direct.options){
            const _included = this.options.some(
            (option) => {
                if (option == _directoption){
                    return true;
                }

                return false;
            });

            if (!_included){
                return false;   // this direct option is not included
            }
        }

        return true;
    }

    /**
     * Quick-config that matches this line, if any. The line does not store the
     * preselect; it is recovered as the first product select for which
     * {@link IsDirect} is true (same product, every select option on the line).
     */
    get shortcut(): PreselectView | null {
        const _selects = this.product?.selects || [];
        for (const _select of _selects){
            if (this.IsDirect(_select)){
                return _select;
            }
        }
        return null;
    }

    /**
     * Text source for the order slip. Prefers the matching shortcut alias,
     * then the product alias, then the product commercial name. The caller
     * resolves display text with `tr(lang)`: alias ignores `lang`; the name
     * uses it. Null when none of those exist.
     */
    get label(): { tr(lang?: string | null): string | null } | null {
        const _alias = this.shortcut?.alias || this.product?.alias || null;
        if (_alias){
            return { tr: () => _alias };
        }
        return this.product?.name ?? null;
    }

    /**
     * Determines if two ticketproducts are equivalent
     * @param ticketproduct The ticketproduct to compary with
     * @returns true if both ticketproducts have the same configuration
     */
    IsJoin(ticketproduct: TicketProductView): boolean {
        if (this.ticketproduct == ticketproduct.ticketproduct){
            return false;   // cannot be the same object
        }

        if (ticketproduct.product != this.product){
            return false;   // is not the same product
        }

        if (ticketproduct.type != this.type){
            return false;   // is not the same routing type
        }

        if (ticketproduct.refund != this.ticketproduct.refund){
            return false;   // is not the refund price
        }

        if (ticketproduct.market != this.ticketproduct.market){
            return false;   // is not the same market price
        }

        if (ticketproduct.weight != this.ticketproduct.weight){
            return false;   // is not the same product weight
        }

        if (ticketproduct.options.length != this.options.length){
            return false;   // not the same number of options
        }
        else {
            const _sameoptions = ticketproduct.options.every(
            productoption => {   // check for the same linked option
                return this.options.some(compareoption => compareoption === productoption);                
            });

            if (!_sameoptions){
                return false;   // not the same options
            }
        }
            
        return true;    // seems safe to be grouped (in the same view)        
    }

    /**
     * Determines if two ticketproducts can be grouped together
     * @param ticketproduct The ticketproduct to compary with
     * @returns true if both ticketproducts can displayed with this one
     */
    IsGroup(ticketproduct: TicketProductView): boolean{
        if (!this.IsJoin(ticketproduct)){
            return false;
        }

        if (ticketproduct.status != this.ticketproduct.status){
            return false;   // not in the same state
        }

        if (ticketproduct.offer || this.offer){
            if (!(ticketproduct.offer && this.offer)){
                return false;   // not the same offer appliance
            }
        }

        // force the price recalculation at this point
        if (ticketproduct.calcinfo.charge != this.calcinfo.charge){
            return false;   // not the same charged price
        }
        
        return true;    // seems safe to be grouped (in the same view)
    }

    /**
     * Determines if two ticketproducts are exactly the same
     * @param ticketproduct The ticketproduct to compary with
     * @returns true if the target ticketproduct can be added into this one
     */    
    IsEqual(ticketproduct: TicketProductView): boolean{
        if (!this.IsGroup(ticketproduct)){
            return false;   // cannot be grouped in the same view item
        }

        if (ticketproduct.offer != this.offer){
            return false;   // not the same ticketoffer applied
        }

        return true;    // seems safe to be grouped (in the same item)
    }

    get Count(): number {
        return this.ticketproduct.amount || 0;
    }

    Remove(count = 1): TicketProductView | null {
        const amount = this.Count
        if ((count == 0) || (amount < count)){
            return null;
        }

        const _notified = Math.min(count, this.ticketproduct.notified || 0);

        // obtain a copy of the ticketproduct with the removed amount
        const _copy = this.ticketproduct.Copy() as TicketProduct;
        if (_copy){
            _copy.CopyOf = null;        // this is not a copy any more

            _copy.amount = count;
            _copy.addlines = 0;
            _copy.dellines = 0;
            _copy.notified = _notified;
            _copy.offer = null;
            _copy.sort = null;
            _copy.charge = _copy.fixed ? (_copy.charge || 0) : null;
            _copy.refund = null;
        }

        // update the current ticketproduct with the updated amount
        this.ticketproduct.amount = amount - count;
        this.ticketproduct.notified = (this.ticketproduct.notified || 0) - _notified;

        return this.alives.get(_copy) as TicketProductView;
    }

    Merge(absorbed: TicketProductView): void {
        if (!absorbed.amount){
            return;
        }

        this.ticketproduct.amount = this.Count + absorbed.amount;
        this.ticketproduct.addlines = (this.ticketproduct.addlines ?? 0) + (absorbed.addlines ?? 0);
        this.ticketproduct.dellines = (this.ticketproduct.dellines ?? 0) + (absorbed.dellines ?? 0);
        this.ticketproduct.notified = (this.ticketproduct.notified ?? 0) + (absorbed.notified ?? 0);

        absorbed.addlines = 0;
        absorbed.dellines = 0;
        absorbed.notified = 0;
    }

    /**********************************/
    /* ORDER NOTIFIED                 */
    /**********************************/

    /**
     * Units still to communicate on the next order slip: `live − notified`.
     * `live` is `0` when the line is cancelled (`DE`), otherwise `amount`.
     * Splitters never participate (always `0`).
     */
    GetNotified(): number {
        if (this.ticketproduct.IsSplitter){
            return 0;
        }

        let _notified = this.ticketproduct.amount || 0;
        if (this.ticketproduct.status === 'DE'){
            _notified = 0;
        }

        return _notified - (this.ticketproduct.notified || 0);
    }

    /**
     * Marks the line as fully communicated after a successful order dispatch.
     * Writes `notified = live` (`0` when `DE`). No-op for splitters.
     */
    SetNotified(): void {
        if (this.ticketproduct.IsSplitter){
            return;
        }

        this.ticketproduct.notified = (this.ticketproduct.amount || 0);

        if (this.ticketproduct.status === 'DE'){
            this.ticketproduct.notified = 0;
        }
    }

    /**********************************/
    /* PRICEINFO PRIVATE METHODS      */
    /**********************************/   
    
    /* obtain the price of the ticketproduct options, resolved by the ticket's place/table/zone/period */
    get __optnsprice(): number {
        let _optnsprice = 0;

        for (const option of this.options){
            _optnsprice += option?.tableprice(this.ticket?.place ?? null, this.ticket?.qrcode ?? null) ?? 0;
        }        

        return _optnsprice;
    }

    /* obtain the fixed for the product */
    get __fixedprice(): number | null {
        if (this.ticketproduct.fixed){
            return this.ticketproduct.charge || 0;
        }

        return null;
    }

    /* obtain the price for the product with options */
    get __prodtprice(): number | null {
        if (!this.product || !this.ticket){
            return null;
        }

        // the base price, resolved by the ticket's place/table/zone/period (falls back to product.price)
        let _prodtprice = this.product.tableprice(this.ticket.place, this.ticket.qrcode);
        
        // market price considerations
        if (this.product.IsMarket){
            _prodtprice = this.ticketproduct.market || 0;
        }

        // weighted product considerations
        if (this.product.IsWeighted){
            _prodtprice = _prodtprice * ((this.ticketproduct.weight || 0) / 1000);
        }

        // the final price is the price + options
        _prodtprice = _prodtprice + this.__optnsprice;

        return _prodtprice;
    }

    /* obtain the final price for the offer */
    get __offerprice(): number | null {
        if (!this.offer){
            return null;
        }

        const _fixed = this.offer.__fixedprice;
        if (_fixed != null){
            return _fixed;
        }

        return this.offer.__offerprice;
    }

    /* obtain the final price for the product in an offer (non offered options), resolved by the ticket's place/table/zone/period */
    get __finalprice(): number | null {
        if (!this.offer){
            return null;
        }

        let _optnsprice = 0;
        for (const ticketoption of this.ticketoptions){
            if (!this.offer.AppliesTo(ticketoption)) {
                const _option = this.alives.get(ticketoption.option) as ProductOptView;
                _optnsprice += _option?.tableprice(this.ticket?.place ?? null, this.ticket?.qrcode ?? null) ?? 0;
            }
        }        
        return _optnsprice;
    }

    /* obtain the price for all the offer products */
    get __totalprice(): number | null {
        if (!this.offer || !this.ticket){
            return null;
        }

        return this.offer.__totalprice;
    }    
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type TicketProductView = ViewType <_TicketProductView, TicketProduct>;

export function TicketProductViewProxy(object: TicketProduct, data: dataService): TicketProductView {
    return new _TicketProductView(object, data).Proxy as TicketProductView;
}
